// ============================================================================
// TIER ENFORCEMENT — Axis Datum
// ============================================================================
// Centralised gate-keeping for subscription tiers.  This module is intentionally
// independent of Zustand so it can be used on both client and server.
// For runtime tier checks in React, prefer the `useSubscriptionStore` hooks.
// ============================================================================

export type SubscriptionTier = 'starter' | 'professional' | 'enterprise' | 'elite';

export interface TierLimits {
  maxProperties: number;
  maxUsers: number;
  maxAssets: number;
  features: string[];
}

// ---------------------------------------------------------------------------
// Feature lists per tier (additive — each tier includes everything below it)
// ---------------------------------------------------------------------------

const STARTER_FEATURES = [
  'work_orders',
  'basic_assets',
  'basic_reports',
  'email_support',
];

const PROFESSIONAL_FEATURES = [
  ...STARTER_FEATURES,
  'preventive_maintenance',
  'inspections',
  'qr_codes',
  'custom_reports',
  'api_access',
  'vendor_portal',
  'offline_mode',
];

const ENTERPRISE_FEATURES = [
  ...PROFESSIONAL_FEATURES,
  'advanced_analytics',
  'sla_tracking',
  'sso',
  'webhooks',
  'stripe_billing',
  'phone_support',
];

const ELITE_FEATURES = [
  ...ENTERPRISE_FEATURES,
  'white_label',
  'custom_development',
  'dedicated_account_manager',
];

// ---------------------------------------------------------------------------
// Tier limits
// ---------------------------------------------------------------------------

export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  starter: {
    maxProperties: 25,
    maxUsers: 5,
    maxAssets: 100,
    features: STARTER_FEATURES,
  },
  professional: {
    maxProperties: 100,
    maxUsers: 25,
    maxAssets: Infinity,
    features: PROFESSIONAL_FEATURES,
  },
  enterprise: {
    maxProperties: Infinity,
    maxUsers: 100,
    maxAssets: Infinity,
    features: ENTERPRISE_FEATURES,
  },
  elite: {
    maxProperties: Infinity,
    maxUsers: Infinity,
    maxAssets: Infinity,
    features: ELITE_FEATURES,
  },
};

// Ordered from lowest to highest
const TIER_ORDER: SubscriptionTier[] = ['starter', 'professional', 'enterprise', 'elite'];

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/**
 * Check if a given tier has access to a named feature.
 */
export function canAccessFeature(tier: SubscriptionTier, feature: string): boolean {
  const limits = TIER_LIMITS[tier];
  if (!limits) return false;
  return limits.features.includes(feature);
}

/**
 * Check if a resource count has reached the tier limit.
 *
 * @param resource  One of 'properties', 'users', or 'assets'.
 * @param currentCount  The current number of that resource.
 * @returns `true` if the organisation is at (or over) the limit.
 */
export function isAtLimit(
  tier: SubscriptionTier,
  resource: 'properties' | 'users' | 'assets',
  currentCount: number,
): boolean {
  const limits = TIER_LIMITS[tier];
  if (!limits) return true;

  const max =
    resource === 'properties' ? limits.maxProperties :
    resource === 'users' ? limits.maxUsers :
    limits.maxAssets;

  return currentCount >= max;
}

/**
 * Build a human-friendly upgrade message for a locked feature.
 */
export function getUpgradeMessage(tier: SubscriptionTier, feature: string): string {
  // Find the minimum tier that includes the feature
  const requiredTier = TIER_ORDER.find((t) => TIER_LIMITS[t].features.includes(feature));

  if (!requiredTier) {
    return 'This feature is not available. Please contact support.';
  }

  if (canAccessFeature(tier, feature)) {
    return ''; // Already has access — no message needed.
  }

  const tierLabel = requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1);
  return `Upgrade to the ${tierLabel} plan to unlock this feature.`;
}

/**
 * Return the minimum tier required for a given feature (or null if none match).
 */
export function getMinimumTier(feature: string): SubscriptionTier | null {
  return TIER_ORDER.find((t) => TIER_LIMITS[t].features.includes(feature)) ?? null;
}
